import { mapPrimeCatalog, mapPrimeUser, mapPrimeUserGroup } from '@/mappers';

export * from './types';

import type { AxiosInstance, AxiosResponse } from 'axios';
import type { IPrimeResponse } from '@/services/prime/types';
import type { IPrimeCatalogResponse, IPrimeUserGroupResponse, IPrimeUserResponse } from '@/services/prime/user/types';
import type { ICatalogData, IUserData, IUserGroupData } from '@/entities';

export class PrimeUserEndpoint {
  axios: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axios = axiosInstance;
  }

  async fetchUserGroups(userId: string): Promise<IUserGroupData[]> {
    const response: AxiosResponse<IPrimeResponse<IPrimeUserGroupResponse[]>> = await this.axios
      .get(`users/${userId}/userGroups`)
      .catch(() => Promise.reject());

    if (response?.data.data) {
      let nextLink = response.data.links?.next;
      const accumulatedUserGroups: IPrimeUserGroupResponse[] = [];
      accumulatedUserGroups.push(...response.data.data);

      while (nextLink) {
        const nextResponse: AxiosResponse<IPrimeResponse<IPrimeUserGroupResponse[]>> = await this.axios
          .get(nextLink)
          .catch(() => Promise.reject());

        if (nextResponse?.data.data) {
          accumulatedUserGroups.push(...nextResponse.data.data);
          nextLink = nextResponse.data.links?.next;
        } else {
          break;
        }
      }

      return accumulatedUserGroups.map((group) => mapPrimeUserGroup(group));
    }

    return Promise.reject();
  }

  async fetchUserCatalogs(): Promise<ICatalogData[]> {
    const response: AxiosResponse<IPrimeResponse<IPrimeCatalogResponse[]>> = await this.axios
      .get(`catalogs`)
      .catch(() => Promise.reject());

    if (response?.data.data) {
      let nextLink = response.data.links?.next;
      const accumulatedCatalogs: IPrimeCatalogResponse[] = [];
      accumulatedCatalogs.push(...response.data.data);

      while (nextLink) {
        const nextResponse: AxiosResponse<IPrimeResponse<IPrimeCatalogResponse[]>> = await this.axios
          .get(nextLink)
          .catch(() => Promise.reject());

        if (nextResponse?.data.data) {
          accumulatedCatalogs.push(...nextResponse.data.data);
          nextLink = nextResponse.data.links?.next;
        } else {
          break;
        }
      }

      return accumulatedCatalogs.map((catalog) => mapPrimeCatalog(catalog));
    }

    return Promise.reject();
  }

  async patchUser(userId: string, attributes: Record<string, string>): Promise<IUserData> {
    const response: AxiosResponse<IPrimeResponse<IPrimeUserResponse>> = await this.axios
      .patch(`users/${userId}`, {
        data: {
          id: userId,
          type: 'user',
          attributes
        }
      })
      .catch(() => Promise.reject());

    if (response?.data.data) {
      return mapPrimeUser(response.data.data);
    }

    return Promise.reject();
  }
}
